import type {
  LeagueBadge,
  LeagueCard,
  LeagueDivision,
  LeagueEarnNow,
  LeagueGradeTopRow,
  LeagueMonth,
  LeagueNextTarget,
  LeagueNextTargetKind,
  LeaguePaginationInfo,
  LeaguePrize,
  LeagueReveal,
  LeagueRevealMovement,
  LeagueRoundBreakdownEntry,
  LeagueRoundFinish,
  LeagueRoundPlay,
  LeagueRoundQuestion,
  LeagueRoundSession,
  LeagueRoundReason,
  LeagueRoundResult,
  LeagueRoundRules,
  LeagueRoundStatus,
  LeagueRoundView,
  LeagueStandingRow,
  LeagueStandings,
  LeagueStudentRef,
  LeagueSummary,
  LeagueSummaryState,
  LeagueTierKey,
  LeagueWeek,
  LeagueWindow,
  LeagueYear,
  LeagueZone,
} from "../types";

type Rec = Record<string, unknown>;

const tierKeys: LeagueTierKey[] = [
  "seed",
  "spark",
  "scholar",
  "vanguard",
  "crown",
];
const nextTargetKinds: LeagueNextTargetKind[] = [
  "start",
  "alone",
  "escapeDrop",
  "reachPromotion",
  "holdLead",
  "reachPodium",
  "passNext",
];
const summaryStates: LeagueSummaryState[] = [
  "off",
  "noGrade",
  "notEnrolled",
  "weekClosing",
  "active",
];

export function rec(value: unknown): Rec | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Rec)
    : null;
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function num(value: unknown, fallback = 0): number {
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;

  const parsed = num(value, Number.NaN);

  return Number.isNaN(parsed) ? null : parsed;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function oneOf<T extends string>(value: unknown, allowed: T[]): T | null {
  return typeof value === "string" && (allowed as string[]).includes(value)
    ? (value as T)
    : null;
}

export function parseStudentRef(value: unknown): LeagueStudentRef | null {
  const source = rec(value);
  const id = numOrNull(source?.id);

  if (!source || id === null) return null;

  const image = rec(source.image);

  return {
    id,
    name: typeof source.name === "string" ? source.name : "",
    image: image && typeof image.url === "string" ? { url: image.url } : null,
  };
}

function parseTier(value: unknown): LeagueTierKey | null {
  return oneOf(value, tierKeys);
}

export function parseDivision(value: unknown): LeagueDivision | null {
  const source = rec(value);
  const tierKey = parseTier(source?.tierKey);

  if (!source || !tierKey) return null;

  return {
    tierKey,
    index: num(source.index, tierKeys.indexOf(tierKey)),
    previousTierKey: parseTier(source.previousTierKey),
    nextTierKey: parseTier(source.nextTierKey),
  };
}

export function parseNextTarget(value: unknown): LeagueNextTarget | null {
  const source = rec(value);

  if (!source) return null;

  return {
    kind: oneOf(source.kind, nextTargetKinds),
    points: Math.max(0, num(source.points)),
    student: parseStudentRef(source.student),
  };
}

function parseZone(value: unknown): LeagueZone | null {
  return oneOf(value, ["promotion", "safe", "drop"]);
}

function parsePrizes(value: unknown): LeaguePrize[] {
  return list(value).flatMap((item) => {
    const source = rec(item);

    return source
      ? [
          {
            place: num(source.place),
            gold: Math.max(0, num(source.gold)),
            diamonds: Math.max(0, num(source.diamonds)),
            badge: source.badge === true,
          },
        ]
      : [];
  });
}

export function parseStandingRow(value: unknown): LeagueStandingRow | null {
  const source = rec(value);
  const student = parseStudentRef(source?.student);
  const rank = numOrNull(source?.rank);

  if (!source || !student || rank === null) return null;

  return {
    rank,
    student,
    points: num(source.points),
    isMe: source.isMe === true,
    zone: parseZone(source.zone),
    tierKey: parseTier(source.tierKey),
    scoredToday: source.scoredToday === true,
  };
}

function parseStandingRows(value: unknown): LeagueStandingRow[] {
  return list(value).flatMap((item) => {
    const row = parseStandingRow(item);

    return row ? [row] : [];
  });
}

function parseGradeTop(value: unknown): LeagueGradeTopRow[] {
  return parseStandingRows(value).map(({ rank, student, points }) => ({
    rank,
    student,
    points,
  }));
}

function parseWeek(value: unknown): LeagueWeek | null {
  const source = rec(value);

  if (!source) return null;

  const promotionPrize = rec(source.promotionPrize);

  return {
    weekKey: str(source.weekKey) ?? "",
    number: numOrNull(source.number),
    startsAt: str(source.startsAt),
    endsAt: str(source.endsAt),
    enrolled: source.enrolled === true,
    rank: numOrNull(source.rank),
    points: num(source.points),
    lastWeekPoints: numOrNull(source.lastWeekPoints),
    groupSize: Math.max(0, num(source.groupSize)),
    poolKey: str(source.poolKey),
    smallGroup: source.smallGroup === true,
    mixedDivisions: source.mixedDivisions === true,
    promoteCount: Math.max(0, num(source.promoteCount)),
    relegateCount: Math.max(0, num(source.relegateCount)),
    zone: parseZone(source.zone),
    nextTarget: parseNextTarget(source.nextTarget),
    gradeRank: numOrNull(source.gradeRank),
    gradeSize: Math.max(0, num(source.gradeSize)),
    gradeTop3: parseGradeTop(source.gradeTop3),
    prizes: parsePrizes(source.prizes),
    promotionPrize: promotionPrize
      ? {
          gold: Math.max(0, num(promotionPrize.gold)),
          diamonds: Math.max(0, num(promotionPrize.diamonds)),
        }
      : null,
  };
}

function parseMonth(value: unknown): LeagueMonth | null {
  const source = rec(value);

  if (!source) return null;

  return {
    monthKey: str(source.monthKey) ?? "",
    endsAt: str(source.endsAt),
    enrolled: source.enrolled === true || numOrNull(source.rank) !== null,
    rank: numOrNull(source.rank),
    points: num(source.points),
    participants: Math.max(0, num(source.participants)),
    nextTarget: parseNextTarget(source.nextTarget),
    top3: parseStandingRows(source.top3),
    prizes: parsePrizes(source.prizes),
  };
}

function parseYear(value: unknown): LeagueYear | null {
  const source = rec(value);

  if (!source) return null;

  const bySource = rec(source.pointsBySource);
  const stats = rec(source.stats);

  return {
    yearKey: str(source.yearKey) ?? "",
    label: str(source.label),
    endsAt: str(source.endsAt),
    enrolled: source.enrolled === true || numOrNull(source.rank) !== null,
    rank: numOrNull(source.rank),
    points: num(source.points),
    participants: Math.max(0, num(source.participants)),
    globalRank: numOrNull(source.globalRank),
    globalParticipants: Math.max(0, num(source.globalParticipants)),
    pointsBySource: {
      video: Math.max(0, num(bySource?.video)),
      quiz: Math.max(0, num(bySource?.quiz)),
      round: Math.max(0, num(bySource?.round)),
    },
    stats: {
      weeksPlayed: Math.max(0, num(stats?.weeksPlayed)),
      bestWeekRank: numOrNull(stats?.bestWeekRank),
      promotions: Math.max(0, num(stats?.promotions)),
    },
    nextTarget: parseNextTarget(source.nextTarget),
    top3: parseStandingRows(source.top3),
    prizes: parsePrizes(source.prizes),
  };
}

function parseRoundReason(value: unknown): LeagueRoundReason | null {
  if (value === null || value === undefined) return null;

  return (
    oneOf<LeagueRoundReason>(value, [
      "trainingGroundOff",
      "noGrade",
      "limitReached",
      "noQuestions",
    ]) ?? "unknown"
  );
}

function parseEarnNow(value: unknown): LeagueEarnNow | null {
  const source = rec(value);

  if (!source) return null;

  const tile = (raw: unknown) => {
    const item = rec(raw);

    return item
      ? {
          pointsEach: Math.max(0, num(item.pointsEach)),
          availableCount: numOrNull(item.availableCount),
        }
      : null;
  };
  const round = rec(source.round);

  return {
    video: tile(source.video),
    quiz: tile(source.quiz),
    round: round
      ? {
          available: round.available === true,
          reason: parseRoundReason(round.reason),
          maxPoints: Math.max(0, num(round.maxPoints)),
          used: Math.max(0, num(round.used)),
          remaining: Math.max(0, num(round.remaining)),
          nextRoundAt: str(round.nextRoundAt),
          activeRoundId: numOrNull(round.activeRoundId),
        }
      : null,
  };
}

export function parseReveal(value: unknown): LeagueReveal | null {
  const source = rec(value);
  const window = oneOf<LeagueWindow>(source?.window, [
    "week",
    "month",
    "year",
  ]);
  const id = str(source?.id);

  if (!source || !window || !id) return null;

  const labels = rec(source.labels);
  const prize = rec(source.prize);

  return {
    id,
    window,
    periodKey: str(source.periodKey) ?? "",
    label: str(source.label) ?? "",
    labels: {
      ar: str(labels?.ar) ?? str(source.label) ?? "",
      en: str(labels?.en) ?? str(source.label) ?? "",
    },
    rank: numOrNull(source.rank),
    of: Math.max(0, num(source.of)),
    points: num(source.points),
    movement: oneOf<LeagueRevealMovement>(source.movement, [
      "promote",
      "stay",
      "relegate",
    ]),
    fromTierKey: parseTier(source.fromTierKey),
    toTierKey: parseTier(source.toTierKey),
    winStreak: Math.max(0, num(source.winStreak)),
    prize: prize
      ? {
          place: numOrNull(prize.place),
          gold: Math.max(0, num(prize.gold)),
          diamonds: Math.max(0, num(prize.diamonds)),
          badgeKeys: list(prize.badgeKeys).filter(
            (key): key is string => typeof key === "string",
          ),
        }
      : null,
    gradeRank: numOrNull(source.gradeRank),
    gradeSize: numOrNull(source.gradeSize),
    globalRank: numOrNull(source.globalRank),
  };
}

export function parseReveals(value: unknown): LeagueReveal[] {
  return list(value).flatMap((item) => {
    const reveal = parseReveal(item);

    return reveal ? [reveal] : [];
  });
}

/**
 * Normalize `GET /league/summary` (full or `view=card`). Never throws:
 * a non-object body yields an `off` summary so callers hide the feature.
 */
export function parseLeagueSummary(value: unknown): LeagueSummary {
  const source = rec(value);

  if (!source || source.enabled !== true) {
    const reason = rec(source)?.reason;

    return {
      enabled: false,
      state: "off",
      reason:
        reason === "gamificationOff" ||
        reason === "leagueOff" ||
        reason === "channelOff"
          ? reason
          : "unknown",
      serverNow: numOrNull(source?.serverNow),
      closingWeekKey: null,
      division: null,
      grade: null,
      week: null,
      month: null,
      year: null,
      earnNow: null,
      pendingReveals: [],
      pendingRevealCount: 0,
    };
  }

  const grade = rec(source.grade);
  const gradeId = numOrNull(grade?.id);
  const pendingReveals = parseReveals(source.pendingReveals);

  return {
    enabled: true,
    state: oneOf(source.state, summaryStates) ?? "unknown",
    reason: null,
    serverNow: numOrNull(source.serverNow),
    closingWeekKey: str(source.closingWeekKey),
    division: parseDivision(source.division),
    grade:
      grade && gradeId !== null
        ? { id: gradeId, name: typeof grade.name === "string" ? grade.name : "" }
        : null,
    week: parseWeek(source.week),
    month: parseMonth(source.month),
    year: parseYear(source.year),
    earnNow: parseEarnNow(source.earnNow),
    pendingReveals,
    pendingRevealCount: Math.max(
      pendingReveals.length,
      Math.max(0, num(source.pendingRevealCount)),
    ),
  };
}

function parsePagination(value: unknown): LeaguePaginationInfo | null {
  const source = rec(value);

  if (!source) return null;

  return {
    page: Math.max(1, num(source.page, 1)),
    limit: Math.max(1, num(source.limit, 20)),
    total: Math.max(0, num(source.total)),
    pages: Math.max(0, num(source.pages)),
    hasNextPage: source.hasNextPage === true,
  };
}

export function parseLeagueStandings(
  value: unknown,
  fallbackWindow: LeagueWindow,
): LeagueStandings {
  const source = rec(value);
  const me = rec(source?.me);
  const meRank = numOrNull(me?.rank);
  const pagination = parsePagination(source?.paginationInfo);
  const rows = parseStandingRows(source?.rows);

  return {
    window:
      oneOf<LeagueWindow>(source?.window, ["week", "month", "year"]) ??
      fallbackWindow,
    scope: oneOf(source?.scope, ["group", "grade", "global"]) ?? "grade",
    periodKey: str(source?.periodKey) ?? "",
    rows,
    me: me && meRank !== null ? { rank: meRank, points: num(me.points) } : null,
    groupSize: Math.max(0, num(source?.groupSize)),
    promoteCount: Math.max(0, num(source?.promoteCount)),
    relegateCount: Math.max(0, num(source?.relegateCount)),
    participants: Math.max(
      0,
      num(source?.participants, pagination?.total ?? rows.length),
    ),
    paginationInfo: pagination,
  };
}

function bilingual(value: unknown): { ar: string; en: string } {
  const source = rec(value);

  return {
    ar: str(source?.ar) ?? str(source?.en) ?? "",
    en: str(source?.en) ?? str(source?.ar) ?? "",
  };
}

export function parseLeagueBadges(value: unknown): LeagueBadge[] {
  const source = rec(value);

  return list(source?.badges).flatMap((item) => {
    const badge = rec(item);
    const key = str(badge?.key);

    if (!badge || !key) return [];

    const visual = rec(badge.badge);

    return [
      {
        key,
        category:
          oneOf<"week" | "month" | "year">(badge.category, [
            "week",
            "month",
            "year",
          ]) ?? "other",
        title: bilingual(badge.title),
        description: bilingual(badge.description),
        icon: str(visual?.icon) ?? "award",
        color: str(visual?.color) ?? "amber",
        earned: badge.earned === true,
        timesEarned: Math.max(0, num(badge.timesEarned)),
        lastEarnedAt: str(badge.lastEarnedAt),
      },
    ];
  });
}

/** Parse one `rewardOutcome.leagueCard`; null when absent or malformed. */
export function parseLeagueCard(value: unknown): LeagueCard | null {
  const source = rec(value);

  if (!source) return null;

  const source_ = oneOf<"video" | "quiz" | "round">(source.source, [
    "video",
    "quiz",
    "round",
  ]);
  const pointsDelta = numOrNull(source.pointsDelta);

  if (pointsDelta === null) return null;

  return {
    source: source_ ?? "unknown",
    pointsDelta,
    weekPoints: num(source.weekPoints),
    rankBefore: numOrNull(source.rankBefore),
    rankAfter: numOrNull(source.rankAfter),
    groupSize: Math.max(0, num(source.groupSize)),
    zone: parseZone(source.zone),
    passed: list(source.passed).flatMap((item) => {
      const student = parseStudentRef(item);

      return student ? [student] : [];
    }),
    monthRank: numOrNull(source.monthRank),
  };
}

function parseCounts(value: unknown) {
  const source = rec(value);

  return {
    easy: Math.max(0, num(source?.easy)),
    medium: Math.max(0, num(source?.medium)),
    hard: Math.max(0, num(source?.hard)),
  };
}

function parseRules(value: unknown): LeagueRoundRules | null {
  const source = rec(value);

  if (!source) return null;

  return {
    questionCount: Math.max(0, num(source.questionCount)),
    counts: parseCounts(source.counts),
    points: parseCounts(source.points),
    maxPoints: Math.max(0, num(source.maxPoints)),
    roundsPerDay: Math.max(0, num(source.roundsPerDay)),
    timeLimitMinutes: Math.max(0, num(source.timeLimitMinutes)),
  };
}

/**
 * `draftAnswers` item shape is not shown in the contract (the examples all
 * use `[]`); accept `{ questionId, answer }` and the `{ id, answer }` alias.
 */
export function parseRoundView(value: unknown): LeagueRoundView | null {
  const source = rec(value);
  const id = numOrNull(source?.id);

  if (!source || id === null) return null;

  return {
    id,
    dayKey: str(source.dayKey),
    slot: numOrNull(source.slot),
    status:
      oneOf<"active" | "scoring" | "finished">(source.status, [
        "active",
        "scoring",
        "finished",
      ]) ?? "unknown",
    startedAt: str(source.startedAt),
    expiresAt: str(source.expiresAt),
    serverNow: numOrNull(source.serverNow),
    sessionId: numOrNull(source.sessionId),
    questionCount: Math.max(0, num(source.questionCount)),
    counts: parseCounts(source.counts),
    draftAnswers: list(source.draftAnswers).flatMap((item) => {
      const draft = rec(item);
      const questionId = numOrNull(draft?.questionId ?? draft?.id);

      return draft && questionId !== null
        ? [{ questionId, answer: draft.answer }]
        : [];
    }),
  };
}

function parseBreakdownEntry(value: unknown): LeagueRoundBreakdownEntry {
  const source = rec(value);

  return {
    correct: Math.max(0, num(source?.correct)),
    total: Math.max(0, num(source?.total)),
    points: Math.max(0, num(source?.points)),
  };
}

export function parseRoundResult(value: unknown): LeagueRoundResult | null {
  const source = rec(value);

  if (!source) return null;

  const breakdown = rec(source.breakdown);

  return {
    correct: Math.max(0, num(source.correct)),
    total: Math.max(0, num(source.total)),
    breakdown: {
      easy: parseBreakdownEntry(breakdown?.easy),
      medium: parseBreakdownEntry(breakdown?.medium),
      hard: parseBreakdownEntry(breakdown?.hard),
    },
    leaguePoints: Math.max(0, num(source.leaguePoints)),
    xp: Math.max(0, num(source.xp)),
    gold: Math.max(0, num(source.gold)),
    finishedAt: str(source.finishedAt),
    expired: source.expired === true,
  };
}

export function parseRoundStatus(value: unknown): LeagueRoundStatus {
  const source = rec(value);
  const today = rec(source?.today);
  const weekly = rec(source?.weekly);
  const reason = parseRoundReason(source?.reason);

  return {
    available: source?.available === true && reason === null,
    reason,
    rules: parseRules(source?.rules),
    today: {
      dayKey: str(today?.dayKey),
      used: Math.max(0, num(today?.used)),
      remaining: Math.max(0, num(today?.remaining)),
      nextRoundAt: str(today?.nextRoundAt),
    },
    active: parseRoundView(source?.active),
    lastResult: parseRoundResult(source?.lastResult),
    weekly: weekly
      ? {
          finished: Math.max(0, num(weekly.finished)),
          target: Math.max(0, num(weekly.target)),
        }
      : null,
  };
}

function parseRoundQuestion(value: unknown): LeagueRoundQuestion | null {
  const source = rec(value);
  const id = numOrNull(source?.id);

  if (!source || id === null) return null;

  const answers = list(source.answers).filter(
    (answer): answer is string | number =>
      typeof answer === "string" || typeof answer === "number",
  );
  const type = oneOf<"mcq" | "boolean" | "fillInTheBlank">(source.type, [
    "mcq",
    "boolean",
    "fillInTheBlank",
  ]);

  return {
    id,
    title: typeof source.title === "string" ? source.title : "",
    description:
      typeof source.description === "string" ? source.description : undefined,
    contentType:
      typeof source.contentType === "string" ? source.contentType : undefined,
    type: type ?? "mcq",
    answers,
    numberOfAnswers: Math.max(0, num(source.numberOfAnswers, answers.length)),
    maxSelections: Math.max(1, num(source.maxSelections, 1)),
    difficulty:
      typeof source.difficulty === "string" ? source.difficulty : "medium",
  };
}

export function parseRoundSession(value: unknown): LeagueRoundSession | null {
  const source = rec(value);
  const id = numOrNull(source?.id);

  if (!source || id === null) return null;

  const questions = list(source.questions).flatMap((item) => {
    const question = parseRoundQuestion(item);

    return question ? [question] : [];
  });

  return {
    id,
    questions,
    totalQuestions: Math.max(0, num(source.totalQuestions, questions.length)),
    answeredCount: Math.max(0, num(source.answeredCount)),
    completed: source.completed === true,
    endsAt: str(source.endsAt),
  };
}

/** Normalize the body of `POST /league/round` or `GET /league/round/:id`. */
export function parseRoundPlay(value: unknown): LeagueRoundPlay | null {
  const source = rec(value);
  const round = parseRoundView(source?.round);

  if (!source || !round) return null;

  return {
    round,
    session: parseRoundSession(source.session),
    result: parseRoundResult(source.result),
    resumed: source.resumed === true,
  };
}

/** Normalize the body of `POST /league/round/:id/finish`. */
export function parseRoundFinish(value: unknown): LeagueRoundFinish | null {
  const source = rec(value);
  const result = parseRoundResult(source?.result);

  if (!source || !result) return null;

  return {
    round: parseRoundView(source.round),
    result,
    leagueCard: parseLeagueCard(rec(source.rewardOutcome)?.leagueCard),
    idempotent: source.idempotent === true,
  };
}
